import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import {
  fetchCustomerList,
  fetchSalesVoucherNumber,
  createSales,
  editSales,
  fetchSpecialPrices,
  fetchDiscountAccounts,
  fetchBatchesForItem,
  fetchPackingStructureForItem,
  fetchPendingSalesOrders,
  fetchSalesOrderWithDetails,
  fetchAvailableTatmeenSerials
} from "./salesThunks";
import { currentDateTime } from "../../lib/functions";

const salesSlice = createSlice({
  name: "sales",
  initialState: {
    // entryDate: dayjs().format("DD/MM/YYYY"),
    entryDate: currentDateTime(),
    item_description: "",
    showDescription: false,
    customersList: null,
    selectedCustomer: null,
    salesVoucherNumber: null,
    defaultAccount: null,
    salesSuccess: null,
    discountAccount: null,
    specialPrices: [],
    tag_field_id: [],
    loading: false,
    error: null,
    ssccCode: "",
    isTatmeenProduct: false,
    batchesList: [],
    loadingBatches: false,
    errorBatches: null,
    packingStructure: [],
    loadingPacking: false,
    errorPacking: null,
    pendingSalesOrders: [],
    selectedSalesOrder: null,
    salesOrderLoading: false,
    isTatmeenRequired: false,
  },
  reducers: {
    // Add any synchronous reducers if needed
    setEntryDate(state, action) {
      state.entryDate = action.payload;
    },
    resetEntryDate(state, action) {
      state.entryDate = currentDateTime();
    },
    setSelectedCustomer: (state, action) => {
      state.selectedCustomer = action.payload;
    },
    setResetSales: (state, action) => {
      state.salesSuccess = null;
      state.error = null;
    },
    setIsTatmeenProduct(state, action) {
      state.isTatmeenProduct = action.payload;
    },
    setSsccCode(state, action) {
      state.ssccCode = action.payload;
    },
    setIsTatmeenRequired(state, action) {
      state.isTatmeenRequired = action.payload;
    },
    setTagFeildNameid(state, action) {
      state.tag_field_id = action.payload || null;
    },
    setSelectedSalesOrder(state, action) {
      state.selectedSalesOrder = action.payload;
    },
    clearSelectedSalesOrder(state) {
      state.selectedSalesOrder = null;
    },
    resetAllSalesState: (state) => {
      state.selectedCustomer = null;
      state.salesVoucherNumber = null;
      state.defaultAccount = null;
      state.salesSuccess = null;
      state.discountAccount = null;
      state.specialPrices = [];
      state.loading = false;
      state.error = null;
      state.batchesList = [];
      state.loadingBatches = false;
      state.errorBatches = null;
    },

  },
  extraReducers: (builder) => {
    builder
      // Handle fetchSalesList
      .addCase(fetchCustomerList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerList.fulfilled, (state, action) => {
        state.loading = false;
        state.customersList = action.payload;
      })
      .addCase(fetchCustomerList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Handle fetch Sales Voucher Number
      .addCase(fetchSalesVoucherNumber.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesVoucherNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.salesVoucherNumber = action.payload.salesVoucherNumber;
        state.defaultAccount = action.payload.defaultAccount;
      })
      .addCase(fetchSalesVoucherNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Handle save sales
      .addCase(createSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSales.fulfilled, (state, action) => {
        state.loading = false;
        state.salesSuccess = action.payload;
      })
      .addCase(createSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })


      // Handle edit sales
      .addCase(editSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editSales.fulfilled, (state, action) => {
        state.loading = false;
        state.salesSuccess = action.payload;
      })
      .addCase(editSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchSpecialPrices.fulfilled, (state, action) => {
        state.specialPrices = action.payload;
      })
      .addCase(fetchSpecialPrices.rejected, (state, action) => {
        state.specialPrices = [];
      })
      .addCase(fetchDiscountAccounts.fulfilled, (state, action) => {
        state.discountAccount = action.payload;
      })

      .addCase(fetchBatchesForItem.pending, (state) => {
        state.loadingBatches = true;
        state.errorBatches = null;
      })
      .addCase(fetchBatchesForItem.fulfilled, (state, action) => {
        state.loadingBatches = false;
        state.batchesList = action.payload || [];
      })
      .addCase(fetchBatchesForItem.rejected, (state, action) => {
        state.loadingBatches = false;
        state.errorBatches = action.payload;
      })
      .addCase(fetchPackingStructureForItem.pending, (state) => {
        state.loadingPacking = true;
        state.errorPacking = null;
      })
      .addCase(fetchPackingStructureForItem.fulfilled, (state, action) => {
        state.loadingPacking = false;
        state.packingStructure = action.payload || [];
      })
      .addCase(fetchPackingStructureForItem.rejected, (state, action) => {
        state.loadingPacking = false;
        state.errorPacking = action.payload;
      })
      .addCase(fetchPendingSalesOrders.pending, (state) => {
        state.salesOrderLoading = true;
      })
      .addCase(fetchPendingSalesOrders.fulfilled, (state, action) => {
        state.salesOrderLoading = false;
        state.pendingSalesOrders = action.payload;
      })
      .addCase(fetchPendingSalesOrders.rejected, (state) => {
        state.salesOrderLoading = false;
        state.pendingSalesOrders = [];
      })

      .addCase(fetchSalesOrderWithDetails.fulfilled, (state, action) => {
        state.selectedSalesOrder = action.payload;
      })


  },
});

export const { setSelectedCustomer, setResetSales, setTagFeildNameid, loadQuotationForSales, setEntryDate, resetEntryDate, setSelectedSalesOrder, clearSelectedSalesOrder, setIsTatmeenProduct, setSsccCode, setIsTatmeenRequired } = salesSlice.actions;
export default salesSlice.reducer;
